import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useListContactsApiV1CampaignsCampaignIdContactsGet } from '#/shared/api/generated/communication/theCargoCommunication'

import {
  CAMPAIGN_CONTACT_FILTERS,
  campaignContactFilterLabelKey,
} from './contact-filters'
import type { CampaignContactFilter } from './contact-filters'

/**
 * The slices the dial menu offers, in the order an agent reaches for them:
 * everyone, then the people still worth a call, then the ones who are done
 * with the sequence one way or another. Every slice is a `filter` the
 * power-dial endpoint takes.
 */
const DIAL_SLICE_GROUPS: readonly (readonly CampaignContactFilter[])[] = [
  [CAMPAIGN_CONTACT_FILTERS.all],
  [
    CAMPAIGN_CONTACT_FILTERS.waiting,
    CAMPAIGN_CONTACT_FILTERS.noResponse,
    CAMPAIGN_CONTACT_FILTERS.inSequence,
    CAMPAIGN_CONTACT_FILTERS.replied,
  ],
  [
    CAMPAIGN_CONTACT_FILTERS.booked,
    CAMPAIGN_CONTACT_FILTERS.heldByCap,
    CAMPAIGN_CONTACT_FILTERS.failed,
    CAMPAIGN_CONTACT_FILTERS.optedOut,
  ],
]

export interface CampaignDialSlice {
  key: CampaignContactFilter
  label: string
  /** People in the slice, before the dialer drops anyone without a mobile
   *  and a quote. `undefined` while the counts load. */
  count: number | undefined
}

/**
 * The dial menu's rows. The counts are the Contact tab's chip counts —
 * `GET /contacts` returns all nine on every page, so one row is asked for —
 * and are read on the menu's first open, not with the header.
 */
export function useCampaignDialSlices(campaignId: string) {
  const { t } = useTranslation()
  const [wanted, setWanted] = useState(false)
  const query = useListContactsApiV1CampaignsCampaignIdContactsGet(
    campaignId,
    { filter: CAMPAIGN_CONTACT_FILTERS.all, page: 1, size: 1 },
    { query: { enabled: wanted } },
  )
  const counts = query.data?.data.counts

  const groups: CampaignDialSlice[][] = DIAL_SLICE_GROUPS.map((group) =>
    group.map((key) => ({
      key,
      label: t(campaignContactFilterLabelKey(key)),
      count: counts?.[key],
    })),
  )

  return {
    groups,
    onOpenChange: (open: boolean) => {
      if (open) setWanted(true)
    },
  }
}
